import { useEffect, useState } from 'react';
import {
  clearFailedPasscodeLoginAttempts,
  clearForcePasscodeReset,
  clearPasscodeSession,
  createPasscodeSession,
  forcePasscodeReset,
  getFailedPasscodeLoginAttempts,
  getSession,
  hasForcePasscodeReset,
  incrementFailedPasscodeLoginAttempts,
  isPasscodeSessionValid,
} from '@ax/service';
import { selectResetSession, useAppSelector } from '@ax/redux-store';

export interface PasscodeSession {
  name: string;
  email: string;
  timestamp: number;
}

export function usePasscodeSession() {
  const resetSession = useAppSelector(selectResetSession);

  const [isSessionInitialized, setIsSessionInitialized] = useState(false);
  const [session, setSession] = useState<PasscodeSession | undefined>(undefined);
  const [forcePasscodeResetValue, setForcePasscodeResetValue] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  useEffect(() => {
    const session = getSession();

    if (session) {
      setSession(session);
    }

    const forceReset = hasForcePasscodeReset();

    if (forceReset) {
      setForcePasscodeResetValue(true);
    }

    setFailedAttempts(getFailedPasscodeLoginAttempts());

    setTimeout(() => {
      setIsSessionInitialized(true);
    });
  }, []);

  useEffect(() => {
    // Listens to resetSession selector changes
    if (resetSession) {
      clearPasscodeSession();
      setSession(undefined);
    }
  }, [resetSession]);

  return {
    session,
    isSessionInitialized,
    hasForcePasscodeReset: forcePasscodeResetValue,
    failedPasscodeLoginAttempts: failedAttempts,
    isSessionValid: isPasscodeSessionValid,
    createSession: (name: string, email: string) => {
      setSession(createPasscodeSession(name, email));
    },
    clearSession: () => {
      clearPasscodeSession();
      setSession(undefined);
    },
    forcePasscodeReset: () => {
      forcePasscodeReset();
      setForcePasscodeResetValue(true);
      clearFailedPasscodeLoginAttempts();
      setFailedAttempts(0);
    },
    clearForcePasscodeReset: () => {
      clearForcePasscodeReset();
      setForcePasscodeResetValue(false);
    },
    incrementFailedPasscodeLoginAttempts: () => {
      setFailedAttempts(incrementFailedPasscodeLoginAttempts());
    },
    clearFailedPasscodeLoginAttempts: () => {
      clearFailedPasscodeLoginAttempts();
      setFailedAttempts(0);
    },
  };
}
